import { ref, computed } from 'vue';
import api from '@/services/api';

const allergies = ref([]);
const currentAllergy = ref(null);
const loading = ref(false);
const error = ref(null);
const pagination = ref({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
});

export function usePatientAllergies() {
    // Configuración
    const API_ENDPOINT = '/patient-allergies';

    /**
     * Listar alergias con filtros y paginación
     */
    const fetchAllergies = async (params = {}) => {
        loading.value = true;
        error.value = null;
        
        try {
            const response = await api.get(API_ENDPOINT, {
                params: {
                    search: params.search || '',
                    patient_id: params.patient_id || null,
                    per_page: params.per_page || 10,
                    page: params.page || 1,
                    sort_column: params.sort_column || 'created_at',
                    sort_order: params.sort_order || 'desc'
                }
            });

            allergies.value = response.data.data;
            
            if (response.data.meta) {
                pagination.value = {
                    current_page: response.data.meta.current_page,
                    last_page: response.data.meta.last_page,
                    per_page: response.data.meta.per_page,
                    total: response.data.meta.total
                };
            }

            return response.data;
        } catch (err) {
            error.value = err.response?.data?.message || 'Error al cargar alergias';
            console.error('Error fetching allergies:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Obtener una alergia específica
     */
    const fetchAllergy = async (id) => {
        loading.value = true;
        error.value = null;
        
        try {
            const response = await api.get(`${API_ENDPOINT}/${id}`);
            currentAllergy.value = response.data.data;
            return response.data.data;
        } catch (err) {
            error.value = err.response?.data?.message || 'Error al cargar la alergia';
            console.error('Error fetching allergy:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Crear nueva alergia
     */
    const createAllergy = async (allergyData) => {
        loading.value = true;
        error.value = null;
        
        try {
            const response = await api.post(API_ENDPOINT, {
                patient_id: allergyData.patient_id,
                allergen: allergyData.allergen,
                allergy_type: allergyData.allergy_type,
                severity: allergyData.severity,
                reaction_type: allergyData.reaction_type
            });

            // Agregar a la lista local
            allergies.value.unshift(response.data.data);
            
            return response.data.data;
        } catch (err) {
            error.value = err.response?.data?.message || 'Error al crear la alergia';
            console.error('Error creating allergy:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Actualizar alergia existente
     */
    const updateAllergy = async (id, allergyData) => {
        loading.value = true;
        error.value = null;
        
        try {
            const response = await api.put(`${API_ENDPOINT}/${id}`, allergyData);

            // Actualizar en la lista local
            const index = allergies.value.findIndex(a => a.id === id);
            if (index !== -1) {
                allergies.value[index] = response.data.data;
            }

            if (currentAllergy.value?.id === id) {
                currentAllergy.value = response.data.data;
            }

            return response.data.data;
        } catch (err) {
            error.value = err.response?.data?.message || 'Error al actualizar la alergia';
            console.error('Error updating allergy:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Eliminar alergia
     */
    const deleteAllergy = async (id) => {
        loading.value = true;
        error.value = null;
        
        try {
            await api.delete(`${API_ENDPOINT}/${id}`);

            // Remover de la lista local
            allergies.value = allergies.value.filter(a => a.id !== id);

            if (currentAllergy.value?.id === id) {
                currentAllergy.value = null;
            }

            return true;
        } catch (err) {
            error.value = err.response?.data?.message || 'Error al eliminar la alergia';
            console.error('Error deleting allergy:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Restaurar alergia eliminada
     */
    const restoreAllergy = async (id) => {
        loading.value = true;
        error.value = null;
        
        try {
            const response = await api.post(`${API_ENDPOINT}/${id}/restore`);
            
            // Agregar de vuelta a la lista
            allergies.value.unshift(response.data.data);
            
            return response.data.data;
        } catch (err) {
            error.value = err.response?.data?.message || 'Error al restaurar la alergia';
            console.error('Error restoring allergy:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Obtener alergias por paciente
     */
    const fetchAllergiesByPatient = async (patientId, params = {}) => {
        return await fetchAllergies({
            ...params,
            patient_id: patientId
        });
    };

    /**
     * Limpiar estado
     */
    const clearAllergies = () => {
        allergies.value = [];
        currentAllergy.value = null;
        error.value = null;
    };

    // Computed
    const hasAllergies = computed(() => allergies.value.length > 0);
    const allergiesCount = computed(() => pagination.value.total);

    return {
        // State
        allergies,
        currentAllergy,
        loading,
        error,
        pagination,

        // Computed
        hasAllergies,
        allergiesCount,

        // Methods
        fetchAllergies,
        fetchAllergy,
        createAllergy,
        updateAllergy,
        deleteAllergy,
        restoreAllergy,
        fetchAllergiesByPatient,
        clearAllergies
    };
}
